import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Timer, Search, Users, User, Ban } from 'lucide-react';
import { formatCATDateTime } from '../../utils/timezone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANGES = [[30, '30 days'], [90, '90 days'], [365, '12 months'], [0, 'All time']];

const Stat = ({ label, value, hint, testId }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid={testId}>
    <p className="text-xs text-gray-500 font-medium">{label}</p>
    <p className="text-2xl font-bold text-gray-800">{value}</p>
    <p className="text-[11px] text-gray-400">{hint}</p>
  </div>
);

const Rank = ({ n }) => (
  <span className={`inline-flex w-7 h-7 items-center justify-center rounded-full text-xs font-bold ${n === 1 ? 'bg-red-100 text-red-700' : n === 2 ? 'bg-orange-100 text-orange-700' : n === 3 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{n}</span>
);

const IntervalState = ({ m }) => {
  if (m.in_cooldown) return <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 text-xs font-medium" title={`Next treatment from ${formatCATDateTime(m.cooldown_ends)}`}>Next treatment {new Date(m.cooldown_ends).toLocaleDateString('en-GB')}</span>;
  return <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs">Eligible now</span>;
};

// Acacia office: members and families with the most billed treatments under the 14-day treatment-interval rule, searchable by ID or name.
export default function CooldownPanel({ authCfg }) {
  const [days, setDays] = useState(90);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { const t = setTimeout(() => setQ(search.trim()), 350); return () => clearTimeout(t); }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/workflow/cooldowns`, { params: { days, q: q || undefined, limit: 25 }, ...authCfg });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load treatment-interval rankings');
    } finally {
      setLoading(false);
    }
  }, [days, q, authCfg]);

  useEffect(() => { load(); }, [load]);

  const members = (data?.members || []).filter((m) => m.periods > 0 || q);
  const families = (data?.families || []).filter((f) => f.periods > 0 || q);
  const waiting = members.filter((m) => m.in_cooldown).length;

  return (
    <div className="space-y-4" data-testid="cooldown-panel">
      <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-col lg:flex-row gap-2 lg:items-center">
        <div className="flex items-center gap-2 text-gray-800 font-semibold mr-auto"><Timer className="w-5 h-5 text-teal-600" /> 14 Days Limit <span className="text-xs font-normal text-gray-500 hidden sm:inline">— 14 days between billed treatments</span></div>
        <div className="relative flex-1 lg:max-w-md">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by member ID (GS-1234-01), family ID (GS-1234) or name" className="pl-9 h-10" data-testid="cooldown-search" />
        </div>
        <div className="flex gap-1" data-testid="cooldown-range">
          {RANGES.map(([d, label]) => (
            <button key={d} onClick={() => setDays(d)} className={`px-3 h-10 rounded-md text-xs font-medium border ${days === d ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} data-testid={`cooldown-range-${d}`}>{label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="Treatments billed" value={(data?.totals?.periods ?? 0).toLocaleString()} hint="Each billed claim starts a 14-day treatment interval" testId="cooldown-total-periods" />
        <Stat label="Members treated" value={data?.member_count ?? '—'} hint="Members with at least one billed treatment in the period" testId="cooldown-total-members" />
        <Stat label="Waiting for next treatment" value={waiting} hint="Listed members still inside their 14-day interval today" testId="cooldown-total-waiting" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        <div className="xl:col-span-3 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2 font-semibold text-gray-800"><User className="w-4 h-4 text-indigo-600" /> Individuals with most treatments <span className="text-xs font-normal text-gray-500">({data?.member_count ?? 0} matched)</span></div>
          {loading && !data ? <div className="p-6 text-center text-sm text-gray-500">Loading…</div> : members.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500" data-testid="cooldown-members-empty">{q ? `No member matches "${q}"` : 'No billed treatments in this period.'}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cooldown-members-table">
                <thead className="bg-gray-50 text-left text-xs text-gray-600 uppercase">
                  <tr><th className="px-3 py-2">#</th><th className="px-3 py-2">Member</th><th className="px-3 py-2 text-center" title="Billed claims in the period — each starts a 14-day treatment interval">Treatments</th><th className="px-3 py-2">Last treatment</th><th className="px-3 py-2">Status</th></tr>
                </thead>
                <tbody>
                  {members.map((m, i) => (
                    <tr key={m.serial_number} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`cooldown-member-${m.serial_number}`}>
                      <td className="px-3 py-2"><Rank n={i + 1} /></td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{m.patient_name || '—'}</div>
                        <div className="text-xs text-gray-500 font-mono">{m.serial_number} • {m.family_id}{m.status && m.status !== 'Active' ? ` • ${m.status}` : ''}</div>
                      </td>
                      <td className="px-3 py-2 text-center font-bold text-gray-800">{m.periods.toLocaleString()}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">{m.last_claim_at ? formatCATDateTime(m.last_claim_at) : '—'}</td>
                      <td className="px-3 py-2"><IntervalState m={m} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="xl:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2 font-semibold text-gray-800"><Users className="w-4 h-4 text-teal-600" /> Families with most treatments <span className="text-xs font-normal text-gray-500">({families.length})</span></div>
          {families.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500" data-testid="cooldown-families-empty">No families to show.</div>
          ) : (
            <table className="w-full text-sm" data-testid="cooldown-families-table">
              <thead className="bg-gray-50 text-left text-xs text-gray-600 uppercase">
                <tr><th className="px-3 py-2">#</th><th className="px-3 py-2">Family</th><th className="px-3 py-2 text-center">Treatments</th><th className="px-3 py-2 text-center">Members</th></tr>
              </thead>
              <tbody>
                {families.map((f, i) => (
                  <tr key={f.family_id} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setSearch(f.family_id)} title="Show this family's members" data-testid={`cooldown-family-${f.family_id}`}>
                    <td className="px-3 py-2"><Rank n={i + 1} /></td>
                    <td className="px-3 py-2"><div className="font-mono font-medium text-gray-900">{f.family_id}</div><div className="text-xs text-gray-500">{f.principal || '—'}{f.status && f.status !== 'Active' ? <span className="ml-1 inline-flex items-center gap-0.5 text-red-600"><Ban className="w-3 h-3" />{f.status}</span> : null}</div></td>
                    <td className="px-3 py-2 text-center font-semibold text-gray-800">{f.periods.toLocaleString()}</td>
                    <td className="px-3 py-2 text-center text-gray-700">{f.members}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <p className="text-[11px] text-gray-400">Ranked by billed treatments in the selected period. "Next treatment" shows the date the member becomes eligible again under the 14-day rule.</p>
    </div>
  );
}
